import axios from 'axios';
import previewPendingReviews from '@/views/response/preview_pending_reviews.json';
import previewReviewsKo from '@/views/response/preview_reviews_ko.json';
import previewReviewsJa from '@/views/response/preview_reviews_ja.json';
import previewReviewsEn from '@/views/response/preview_reviews_en.json';
import previewSocialReviewsKo from '@/views/response/preview_social_reviews_ko.json';
import previewSocialReviewsJa from '@/views/response/preview_social_reviews_ja.json';
import previewSocialReviewsEn from '@/views/response/preview_social_reviews_en.json';
import previewWrittenReviews from '@/views/response/preview_written_reviews.json';

const previewReviewsByLocale = {
  'ko-KR': previewReviewsKo,
  'ja-JP': previewReviewsJa
};

const previewSocialReviewsByLocale = {
  'ko-KR': previewSocialReviewsKo,
  'ja-JP': previewSocialReviewsJa
};

class Api {
  constructor(brandCode, sessionParams, preview, dummy, locale) {
    this.sessionParams = sessionParams;
    this.api = axios.create({ baseURL: `/api/${brandCode}` });
    this.preview = preview;
    this.dummy = dummy;
    this.locale = locale;

    if (preview) {
      this.api.interceptors.request.use(config => {
        if (config.method !== 'get') {
          return {
            ...config,
            adapter: () => Promise.reject()
          };
        }
        return config;
      });
    }
  }

  get(url, params) {
    return this.api.get(url, { params: { ...this.sessionParams, ...params } });
  }

  delete(url, params) {
    return this.api.delete(url, {
      params: { ...this.sessionParams, ...params }
    });
  }

  post(url, data, params = {}) {
    return this.api.post(url, data, {
      params: { ...this.sessionParams, ...params }
    });
  }

  patch(url, data) {
    return this.api.patch(url, data, { params: this.sessionParams });
  }

  fetchReviewSettings(isMobile) {
    return this.get('/review_settings', { mobile: isMobile ? '1' : '0' });
  }

  fetchReviewPolicy() {
    return this.get('/review_policy');
  }

  fetchWidget({
    id,
    parent_widget_id,
    widget_type,
    widget_style,
    mobile,
    preview,
    original_widget_id,
    parent_url,
    install_method
  }) {
    return this.get('/widgets', {
      id,
      parent_widget_id,
      widget_type,
      widget_style,
      mobile,
      preview,
      original_widget_id,
      parent_url,
      install_method
    });
  }

  fetchSession() {
    return this.get('/sessions');
  }

  fetchBrandNotices() {
    return this.get('/brand_notices');
  }

  fetchNoticeSummary() {
    return this.get('/notice_summary');
  }

  fetchReviews(params) {
    if (!this.dummy) return this.get('/reviews', params);
    const isSocialWidget = params?.fields?.includes('social_media_title');
    return this.get('/reviews', params).then(response => {
      if (response?.data?.reviews?.length > 0) return response;
      if (isSocialWidget) {
        return Promise.resolve(
          previewSocialReviewsByLocale[this.locale] || previewSocialReviewsEn
        );
      } else {
        return Promise.resolve(
          previewReviewsByLocale[this.locale] || previewReviewsEn
        );
      }
    });
  }

  showReview(id, params) {
    return this.get(`/reviews/${id}`, params);
  }

  newReview(params) {
    return this.get('/reviews/new', params);
  }

  editReview(id) {
    return this.get(`/reviews/${id}/edit`);
  }

  createReview(product_id, sub_order_id, formData) {
    return this.post('/reviews', formData, { product_id, sub_order_id });
  }

  updateReview(id, formData) {
    return this.patch(`/reviews/${id}`, formData);
  }

  deleteReview(id, password) {
    return this.delete(`/reviews/${id}`, { password });
  }

  fetchReviewWritableCheck(product_code) {
    return this.get('/review_writable_checks', { product_code });
  }

  checkReviewDisabled() {
    return this.get('/review_writable_checks/check_disabled');
  }

  markReviewDisabled() {
    return this.post('/review_writable_checks/mark_disabled');
  }

  checkReviewPassword(id, password) {
    return this.post(`/reviews/${id}/check_password`, { password });
  }

  fetchComments(review_id) {
    return this.get('/comments', { review_id });
  }

  createComment(review_id, data) {
    return this.post('/comments', data, { review_id });
  }

  updateComment(id, data) {
    return this.patch(`/comments/${id}`, data);
  }

  deleteComment(id) {
    return this.delete(`/comments/${id}`);
  }

  createLike(review_id, score) {
    return this.post('/review_likes', { review_id, score });
  }

  deleteLike(review_id) {
    return this.delete(`/review_likes/${review_id}`);
  }

  createSpamReport(spam_report) {
    return this.post('/spam_reports', { spam_report });
  }

  fetchProducts(params) {
    return this.get('/products', params);
  }

  fetchProductCategories(product_code, widget_id) {
    return this.get('/categories', { widget_id, product_code });
  }

  fetchProductMediaReviews(product_code, widgetParams) {
    return this.get('/product_media_reviews', {
      ...widgetParams,
      product_code
    });
  }

  fetchProductScore(product_code, widget_id, fields) {
    return this.get('/product_score', { widget_id, product_code, fields });
  }

  fetchReviewsSummary(product_code) {
    return this.get('/reviews_summary', { product_code });
  }

  fetchProductOptionTypes(product_code, fields) {
    return this.get('/product_option_types', { product_code, fields });
  }

  fetchReviewOptionTypes(product_code) {
    return this.get('/review_option_types', { product_code });
  }

  fetchEvaluationTypeStatistics(product_code) {
    return this.get('/evaluation_type_statistics', { product_code });
  }

  fetchReviewTagTypes(product_code) {
    return this.get('/review_tag_types', { product_code });
  }

  fetchProductStatistics(product_code, widget_id) {
    return this.get('/products/review_stats', { widget_id, product_code });
  }

  createVideo(blob_id) {
    return this.post('/videos', { blob_id });
  }

  getVideoDirectUploadUrl() {
    return `${this.api.defaults.baseURL}/video_direct_uploads`;
  }

  fetchTranslations(locale) {
    return this.get('/translations', { locale });
  }

  fetchPendingReviews() {
    return this.get('/sub_orders/pending_reviews');
  }

  fetchReviewPrompt() {
    return this.get('/review_prompt');
  }

  resolveSubOrder(params) {
    return this.get('/sub_orders/pending_reviews/resolve', params);
  }

  fetchManagingReviewsPendingReviews(params) {
    if (this.preview) {
      const previewData = previewPendingReviews;
      const expiresDate = new Date();
      expiresDate.setDate(expiresDate.getDate() + 30);

      previewData.data.sub_orders.forEach(order => {
        order.pending_review_expires_in = expiresDate;
      });

      return Promise.resolve(previewData);
    }

    return this.get('/managing_reviews/pending_reviews', params);
  }

  fetchManagingReviewsWrittenReviews(params) {
    if (this.preview) {
      return Promise.resolve(previewWrittenReviews);
    }

    return this.get('/managing_reviews/written_reviews', params);
  }

  fetchManagingReviewsCount() {
    if (this.preview)
      return Promise.resolve({
        data: { pending_reviews_count: 3, written_reviews_count: 2 }
      });

    return this.get('/managing_reviews/reviews_count');
  }

  fetchDisplayableWithCategory({ widget_id, category_code, product_code }) {
    return this.get('/categories/displayable_check', {
      widget_id,
      product_code,
      category_code
    });
  }

  fetchFacebooks() {
    return this.get('/facebooks');
  }

  fetchUserPropertyFilterShowable({ product_code }) {
    return this.get('/user_properties/check_filter_showable', { product_code });
  }

  fetchUserProperties({ username, secure_username, product_code }) {
    return this.get('/user_properties', {
      username,
      secure_username,
      product_code
    });
  }

  fetchProductSummary(product_code) {
    return this.get('/products/summary', { product_code });
  }

  fetchSizeRecommendationDisplayableCheck(product_code) {
    return this.get('/size_recommendations/displayable_check', {
      product_code
    });
  }

  fetchSizeRecommendation({ product_code, height, weight, fit_type }) {
    return this.get('/size_recommendations', {
      product_code,
      height,
      weight,
      fit_type
    });
  }

  fetchSizeFeedbacks({ product_code, page, filters }) {
    return this.get('/size_feedbacks', { product_code, page, ...filters });
  }

  fetchSizeFeedbackFilters(product_code) {
    return this.get('/size_feedback_filters', { product_code });
  }

  fetchProductReviewThumbnails({ review_id, product_code, page, fields }) {
    return this.get('/products/review_thumbnails', {
      review_id,
      product_code,
      page,
      fields
    });
  }

  fetchMileageMessage() {
    return this.get('/mileage_messages');
  }

  clickMoreReviews(id) {
    return this.post('/widgets/click_more_reviews', {
      id
    });
  }

  targetClick(cohort_item_id) {
    return this.post('/managing_reviews/target_click', { cohort_item_id });
  }

  fetchKeywordsAnalysis(review_message, keywords) {
    return this.get('/keywords_analysis', { review_message, keywords });
  }

  analyzeAiClothingImage(data) {
    return this.post('/clothing_image_analysis', data);
  }
}
export default Api;
